<template>
    <div class="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <!-- Main column -->
        <div class="xl:col-span-8 flex flex-col gap-6 min-w-0">
            <!-- Price chart -->
            <section class="qp-card p-4 sm:p-6">
                <div class="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <div>
                        <h2 class="text-[15px] font-semibold tracking-[-0.01em] text-ink">{{ $t('Price history') }}</h2>
                        <div v-if="rangePerf" class="qp-num text-[13px] font-medium mt-1" :class="toneText(rangePerf.change)">
                            {{ fmt.signed(rangePerf.change) }} ({{ fmt.pct(rangePerf.pct, 2, { signed: true }) }})
                            <span class="font-normal text-faint">· {{ $t(rangeLabels[range]) }}</span>
                        </div>
                    </div>
                    <div class="flex flex-wrap items-center gap-2">
                        <div class="qp-seg" role="group" :aria-label="$t('Range')">
                            <button v-for="r in ranges" :key="r" type="button" :class="{ 'is-active': r === range }"
                                @click="range = r">{{ r }}</button>
                        </div>
                        <div class="qp-seg" role="group" :aria-label="$t('Chart type')">
                            <button v-for="t in chartTypes" :key="t.value" type="button" :title="$t(t.label)"
                                :aria-label="$t(t.label)" :class="{ 'is-active': t.value === chartType }"
                                @click="chartType = t.value">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"
                                    v-html="t.icon"></svg>
                            </button>
                        </div>
                    </div>
                </div>
                <div class="h-[360px] sm:h-[440px] relative">
                    <div v-if="chartLoader" class="absolute inset-0 flex items-center justify-center">
                        <Loading />
                    </div>
                    <div v-else-if="!candleChartData || !candleChartData.length"
                        class="absolute inset-0 flex items-center justify-center text-[13px] text-faint">
                        {{ $t('No price history available') }}
                    </div>
                    <CandlestickChart v-else :sampleDatas="candleChartData" :chartType="chartType" :range="range"
                        :earnings="earningsData" />
                </div>
            </section>

            <!-- Snapshot -->
            <section class="qp-card p-4 sm:p-6">
                <HeadingComp :text="$t('Snapshot')" :subtitle="$t('Technicals, valuation and fundamentals at a glance')" />
                <StockTable :metricData="metricData" :priceMetricsData="priceMetricsData"
                    :companyProfileData="companyProfileData" :price="price" :extraData="extraData" />
            </section>

            <!-- Financials -->
            <section class="qp-card p-4 sm:p-6">
                <HeadingComp :text="$t('Performance')" :subtitle="$t('Quarterly revenue, net income and net margin')" />
                <div v-if="miniChartLoader" class="h-[300px] flex items-center justify-center"><Loading /></div>
                <p v-else-if="!hasFinancials" class="h-[160px] flex items-center justify-center text-center text-[13px] text-faint">
                    {{ financialsError ? $t('financials-error') : $t('No financial statements for this symbol') }}
                </p>
                <DebtCharts v-else :DebtChartData="performanceChartData" />
            </section>

            <div v-if="miniChartLoader || hasFinancials || hasEstimates" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <section class="qp-card p-4 sm:p-6 min-w-0">
                    <HeadingComp :text="$t('Revenue to profit conversion')" :subtitle="$t('Latest quarter')" />
                    <div v-if="miniChartLoader" class="h-[300px] flex items-center justify-center"><Loading /></div>
                    <p v-else-if="!hasFinancials" class="h-[160px] flex items-center justify-center text-[13px] text-faint">
                        {{ $t('No financial statements for this symbol') }}
                    </p>
                    <ProfitConservationCharts v-else :conservationChartData="profitConversionChartData" />
                </section>
                <section class="qp-card p-4 sm:p-6 min-w-0">
                    <HeadingComp :text="$t('Revenue Estimates')" :subtitle="$t('Reported vs. consensus estimate')" />
                    <div v-if="miniChartLoader" class="h-[300px] flex items-center justify-center"><Loading /></div>
                    <p v-else-if="!hasEstimates" class="h-[160px] flex items-center justify-center text-[13px] text-faint">
                        {{ $t('No estimates for this symbol') }}
                    </p>
                    <RoundedChart v-else :renderingData="estimateSeries" :renderingYear="estimateYears" />
                </section>
            </div>
        </div>

        <!-- Sidebar -->
        <aside class="xl:col-span-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-1 gap-6 content-start items-start min-w-0">
            <!-- Upcoming earnings -->
            <section class="qp-card p-4 sm:p-6">
                <HeadingComp :text="$t('Upcoming earnings')" />
                <div v-if="earnings.date" class="flex items-center gap-4 mb-3">
                    <div class="w-[56px] shrink-0 rounded-xl overflow-hidden ring-1 ring-line-card text-center bg-surface">
                        <div class="bg-brand text-white text-[10px] font-semibold uppercase tracking-[0.08em] py-1">
                            {{ earnings.month }}
                        </div>
                        <div class="qp-num text-[22px] font-semibold text-ink py-1.5">{{ earnings.day }}</div>
                    </div>
                    <div class="min-w-0">
                        <div class="text-[15px] font-semibold text-ink">{{ earnings.full }}</div>
                        <div class="text-[12px] text-muted mt-0.5">
                            <span v-if="earnings.session">{{ earnings.session }}</span>
                            <span v-if="earnings.session && earnings.days !== null"> · </span>
                            <span v-if="earnings.days !== null">{{ earnings.daysText }}</span>
                        </div>
                    </div>
                </div>
                <p v-else class="text-[13px] text-faint mb-2">{{ $t('No upcoming earnings date') }}</p>
                <StatList :rows="earningsRows" />
            </section>

            <!-- Earnings history -->
            <EarningsHistory v-if="showEarningsHistory" :rows="earningsHistoryRows"
                :loading="chartLoader && !earningsHistoryRows.length" />

            <!-- About -->
            <section class="qp-card p-4 sm:p-6">
                <HeadingComp :text="$t('About the company')" :subtitle="companyProfileData?.name || ''" />
                <p v-if="companyProfileData?.description"
                    class="text-[14px] leading-[1.65] text-ink-2 mb-1" :class="lineClamp ? 'line-clamp-4' : ''">
                    {{ companyProfileData.description }}
                </p>
                <button v-if="companyProfileData?.description" type="button" @click="lineClamp = !lineClamp"
                    class="text-[13px] font-medium text-brand hover:underline mb-3">
                    {{ lineClamp ? $t('Read more') : $t('Read less') }}
                </button>
                <StatList :rows="profileRows" />
            </section>

            <section class="qp-card p-4 sm:p-6">
                <HeadingComp :text="$t('Dividends')" />
                <p v-if="!currentDividend" class="text-[13px] text-faint mb-2">{{ $t('No recent dividend') }}</p>
                <p v-else-if="dividendStale" class="text-[12px] text-notice bg-notice-bg rounded-lg px-3 py-2 mb-2">
                    {{ $t('dividend-stale', { date: fmt.date(currentDividend.date) }) }}
                </p>
                <StatList :rows="dividendRows" />
            </section>
        </aside>

        <!-- Peers -->
        <section class="xl:col-span-12 qp-card p-4 sm:p-6">
            <HeadingComp :text="$t('Peers')" :subtitle="$t('Companies in the same industry')" />
            <div v-if="!peers.length" class="py-10 text-center text-[13px] text-faint">
                {{ $t('Peers data not found') }}
            </div>
            <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3">
                <button v-for="item in peers" :key="item.ticker" type="button" @click="toggaleSymbol(item.ticker)"
                    class="text-left flex items-center gap-3 rounded-xl border border-line-card bg-surface px-4 py-3 hover:border-brand/40 hover:shadow-[0_6px_20px_rgba(15,23,42,0.06)] transition">
                    <img v-if="item.logo" :src="item.logo" :alt="item.ticker"
                        class="h-9 w-9 rounded-lg object-contain ring-1 ring-line-soft bg-white p-0.5 shrink-0" />
                    <span v-else
                        class="h-9 w-9 rounded-lg bg-surface-3 text-[14px] font-semibold text-ink-2 inline-flex items-center justify-center shrink-0">
                        {{ item.ticker[0] }}
                    </span>
                    <div class="min-w-0 flex-1">
                        <div class="text-[14px] font-semibold text-ink">{{ item.ticker }}</div>
                        <div class="text-[12px] text-muted truncate">{{ item.name }}</div>
                    </div>
                    <div class="text-right shrink-0">
                        <div class="qp-num text-[14px] font-medium text-ink">{{ fmt.price(item.price) }}</div>
                        <div class="qp-num text-[12px] font-medium" :class="toneText(item.changePct)">
                            {{ fmt.pct(item.changePct, 2, { signed: true }) }}
                        </div>
                    </div>
                </button>
            </div>
        </section>
    </div>
</template>

<script>
import HeadingComp from '../HeadingComp.vue'
import StatList from '../StatList.vue'
import StockTable from '../StockTable.vue'
import CandlestickChart from '../ApacheChar.vue'
import DebtCharts from '../Charts/DebtCharts.vue'
import ProfitConservationCharts from '../Charts/ProfitConservationCharts.vue'
import RoundedChart from '../Charts/RoundedChart.vue'
import Loading from '@/components/loading/Loading.vue'
import EarningsHistory from '../EarningsHistory.vue'
import { formatMixin, isNum, DASH, moneyCompact } from '@/utils/format'
import { CHART_RANGES, rangeChange } from '@/utils/candles'
import { earningsReturns, averageMove } from '@/utils/earningsHistory'

const ICONS = {
    candlestick: '<path d="M4.5 2v2.5M4.5 10.5V14M11.5 2v4M11.5 11v3" stroke="currentColor" stroke-linecap="round"/><rect x="3" y="4.5" width="3" height="6" rx="0.8" stroke="currentColor"/><rect x="10" y="6" width="3" height="5" rx="0.8" fill="currentColor" stroke="currentColor"/>',
    line: '<path d="M1.5 12 5.5 7.5l3 2.5 6-7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
    area: '<path d="M1.5 12 5.5 7.5l3 2.5 6-7V14.5h-13z" fill="currentColor" opacity=".25"/><path d="M1.5 12 5.5 7.5l3 2.5 6-7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
}

export default {
    name: 'QuoteOverview',
    mixins: [formatMixin],
    components: {
        HeadingComp,
        StatList,
        StockTable,
        CandlestickChart,
        DebtCharts,
        ProfitConservationCharts,
        RoundedChart,
        Loading,
        EarningsHistory,
    },
    props: {
        companyProfileData: { type: Object, default: () => ({}) },
        metricData: { type: Object, default: () => ({}) },
        priceMetricsData: { type: Object, default: () => ({}) },
        currentDividend: { type: Object, default: null },
        performanceChartData: { type: Object, default: () => ({}) },
        profitConversionChartData: { type: [Array, Object], default: () => [] },
        EstimatechartsData: { type: [Object, Array], default: () => ({}) },
        perrsData: { type: [Array, Object], default: () => [] },
        candleChartData: { type: Array, default: () => [] },
        chartLoader: { type: Boolean, default: true },
        miniChartLoader: { type: Boolean, default: true },
        figiNumber: { type: String, default: '' },
        price: { type: Object, default: () => ({}) },
        extraData: { type: Object, default: () => ({}) },
        currentEarningsData: { type: Object, default: () => ({}) },
        // Option-implied move for the next report (earningsMove of /stock/option-term-structure); null without options.
        earningsMove: { type: Object, default: null },
        // Full earnings calendar (past and upcoming) for the price chart markers.
        earningsData: { type: Array, default: () => [] },
        symbol: { type: String, default: '' },
        financialsError: { type: Boolean, default: false },
    },
    data() {
        return {
            lineClamp: true,
            chartType: 'candlestick',
            range: '1Y',
            ranges: CHART_RANGES,
            rangeLabels: {
                '1M': 'Past month',
                '3M': 'Past 3 months',
                '6M': 'Past 6 months',
                YTD: 'Year to date',
                '1Y': 'Past year',
                '5Y': 'Past 5 years',
                MAX: 'All available history',
            },
            chartTypes: [
                { value: 'candlestick', label: 'Candlestick Chart', icon: ICONS.candlestick },
                { value: 'line', label: 'Line Chart', icon: ICONS.line },
                { value: 'area', label: 'Area chart', icon: ICONS.area },
            ],
        }
    },
    computed: {
        locale() {
            return this.$i18n ? this.$i18n.locale : 'en'
        },
        rangePerf() {
            return rangeChange(this.candleChartData, this.range)
        },
        earnings() {
            const e = this.currentEarningsData || {}
            if (!e.date) return { date: null }
            const d = new Date(String(e.date).slice(0, 10) + 'T00:00:00')
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            const days = isNaN(d) ? null : Math.round((d - today) / 86400000)
            let daysText = ''
            if (days === 0) daysText = this.$t('Today')
            else if (days === 1) daysText = this.$t('Tomorrow')
            else if (days > 1) daysText = this.$t('in {n} days', { n: days })
            return {
                date: e.date,
                month: this.fmt.date(e.date, { month: 'short' }).replace('.', ''),
                day: this.fmt.date(e.date, { day: 'numeric' }),
                full: this.fmt.date(e.date, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
                session: e.hour === 'amc' ? this.$t('After close') : e.hour === 'bmo' ? this.$t('Before open')
                    : e.hour === 'dmh' ? this.$t('During market hours') : '',
                days: days !== null && days >= 0 ? days : null,
                daysText,
            }
        },
        earningsRows() {
            const e = this.currentEarningsData || {}
            const year = e.year || (e.date ? String(e.date).slice(0, 4) : '')
            const rows = [
                { label: this.$t('Report period'), value: e.quarter ? `Q${e.quarter} ${year}` : DASH },
                { label: this.$t('EPS estimate'), value: this.fmt.money(e.epsEstimate) },
                { label: this.$t('Revenue estimate'), value: moneyCompact(e.revenueEstimate) },
            ]
            const withMove = this.earningsMove ? rows.concat(this.earningsMoveRows) : rows
            const avg = averageMove(this.earningsHistoryRows)
            return avg
                ? withMove.concat({
                    label: this.$t('Avg. 1-day move'),
                    value: `±${this.fmt.pct(avg.pct, 1)}`,
                    hint: this.$t('avg-move-hint', { n: avg.count }),
                })
                : withMove
        },
        // Past reports with the stock's 1/5/21-day returns, newest first.
        earningsHistoryRows() {
            return earningsReturns(this.earningsData, this.candleChartData)
        },
        // Hidden for symbols that never reported (ETFs) or have no price history once loaded.
        showEarningsHistory() {
            if (this.earningsHistoryRows.length) return true
            const today = new Date().toISOString().slice(0, 10)
            const reported = (this.earningsData || []).some((e) => String(e?.date || '').slice(0, 10) <= today)
            return this.chartLoader && reported
        },
        // Shown only when the options move was computed for the same report date as the one displayed above.
        earningsMoveRows() {
            const m = this.earningsMove || {}
            const shown = String(this.currentEarningsData?.date || '').slice(0, 10)
            const has = !!shown && m.earningsDate === shown && m.move > 0
            if (!has) {
                return [
                    { label: this.$t('Market Maker Move'), value: DASH },
                    { label: this.$t('Expected range'), value: DASH },
                ]
            }
            const asOf = this.fmt.date(m.chainDate, { month: 'short', day: 'numeric' })
            const hint = [this.$t('mmm-hint', { date: asOf }), m.precision === 'monthly' ? this.$t('mmm-monthly') : null]
                .filter(Boolean).join(' · ')
            return [
                { label: this.$t('Market Maker Move'), value: `±${this.fmt.money(m.move, 2)} (±${this.fmt.pct(m.movePct, 1)})`, hint },
                { label: this.$t('Expected range'), value: `${this.fmt.money(m.low)} – ${this.fmt.money(m.high)}` },
            ]
        },
        profileRows() {
            const c = this.companyProfileData || {}
            const titleCase = (s) => String(s || '').toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase())
            const hq = [c.city, titleCase(c.state), c.country].filter(Boolean).join(', ')
            return [
                { label: this.$t('Sector'), value: c.gsector || DASH },
                { label: this.$t('Industry'), value: c.finnhubIndustry || DASH },
                { label: this.$t('IPO'), value: this.fmt.date(c.ipo) },
                { label: this.$t('Website'), value: c.weburl ? this.domain(c.weburl) : DASH, href: c.weburl || null },
                { label: this.$t('Headquarters'), value: hq || DASH },
                { label: this.$t('Employees'), value: this.fmt.num(c.employeeTotal, 0) },
                { label: this.$t('Phone'), value: c.phone ? this.formatPhoneNumber(c.phone) : DASH },
                { label: 'FIGI', value: this.figiNumber || DASH },
            ]
        },
        // The single home of dividend figures on the page.
        dividendRows() {
            const d = this.currentDividend || {}
            const m = this.metricData || {}
            const f = this.fmt
            return [
                { label: this.$t('Latest dividend'), value: isNum(d.amount) ? `${f.money(d.amount, this.dividendDecimals(d.amount))} ${this.$t('per share')}` : DASH },
                { label: this.$t('Ex-dividend date'), value: f.date(d.date) },
                { label: this.$t('Payment date'), value: f.date(d.payDate) },
                { label: this.$t('Dividend TTM'), value: f.money(m.dividendPerShareTTM) },
                { label: this.$t('Dividend yield (TTM)'), value: f.pct(m.currentDividendYieldTTM) },
                { label: this.$t('Payout'), value: f.pct(m.payoutRatioTTM) },
                { label: this.$t('5Y dividend growth'), value: f.pct(m.dividendGrowthRate5Y, 2, { signed: true }), tone: f.tone(m.dividendGrowthRate5Y) },
            ]
        },
        // A payer that stopped: the latest ex-date is more than ~15 months old.
        dividendStale() {
            const m = String(this.currentDividend?.date || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
            if (!m) return false
            const exDate = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
            return (Date.now() - exDate.getTime()) / 86400000 > 460
        },
        hasFinancials() {
            return ((this.performanceChartData || {}).performanceDataRevenue || []).length > 0
        },
        hasEstimates() {
            return ((this.EstimatechartsData || {}).EpsYear || []).length > 0
        },
        estimateSeries() {
            const d = this.EstimatechartsData || {}
            return [
                { name: this.$t('Reported'), data: d.EpsActual || [] },
                { name: this.$t('Estimate'), data: d.EpsEsitimate || [] },
            ]
        },
        estimateYears() {
            return (this.EstimatechartsData || {}).EpsYear || []
        },
        peers() {
            const list = Array.isArray(this.perrsData) ? this.perrsData : []
            return list
                .map((item) => ({
                    ticker: item?.companyProfile?.ticker || item?.symbol,
                    name: item?.companyProfile?.name || '',
                    logo: item?.companyProfile?.logo || '',
                    price: item?.stockQuote?.currentPrice,
                    changePct: item?.stockQuote?.priceChangePercent,
                }))
                .filter((p) => p.ticker && p.ticker !== this.symbol)
        },
    },
    methods: {
        // $0.27 rather than $0.2700, but keep $0.0625 exact.
        dividendDecimals(v) {
            const n = Number(v)
            return Math.abs(n * 100 - Math.round(n * 100)) < 1e-9 ? 2 : 4
        },
        toneText(v) {
            const t = this.fmt.tone(v)
            if (t === 'up') return 'text-up'
            if (t === 'down') return 'text-down'
            return 'text-muted'
        },
        domain(url) {
            try {
                return new URL(url).hostname.replace(/^www\./, '')
            } catch (e) {
                return String(url).replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]
            }
        },
        formatPhoneNumber(phone) {
            const cleaned = String(phone).replace(/\D/g, '')
            if (cleaned.length === 11 && cleaned[0] === '1') {
                return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
            }
            if (cleaned.length === 10) {
                return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
            }
            return `+${cleaned}`
        },
        toggaleSymbol(symbol) {
            this.$emit('getSymbol', symbol)
        },
    },
}
</script>
